package com.rosario.juridico.ui.screens

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.Chat
import androidx.compose.material.icons.filled.*
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.rosario.juridico.openWhatsApp
import com.rosario.juridico.openEmail

data class InfoItem(val question: String, val answer: String, val icon: ImageVector)

@Composable
fun PantallaInfo() {
    val infoItems = remember {
        listOf(
            InfoItem(
                "Acceso y Usos Gratuitos",
                "Jurídico Rosario ofrece un modelo de cortesía que incluye 5 usos iniciales de bienvenida. Una vez agotados, dispondrás de 1 uso gratuito cada mes calendario. Para acceso ilimitado, podés activar la membresía Premium.",
                Icons.Default.Timer
            ),
            InfoItem(
                "Suscripción Premium",
                "La membresía Premium desbloquea el acceso ilimitado a todas las funciones. En la App Android, el pago se procesa de forma segura a través de Google Play Store. En la versión Web, utilizamos Mercado Pago como pasarela oficial.",
                Icons.Default.WorkspacePremium
            ),
            InfoItem(
                "Sincronización Multiplataforma",
                "¡Tu suscripción es única! Si activas Premium en tu celular, también lo tendrás disponible en la versión Web (y viceversa). Solo asegúrate de iniciar sesión con el mismo correo electrónico en ambos dispositivos.",
                Icons.Default.Sync
            ),
            InfoItem(
                "Activación Inmediata",
                "Una vez confirmado el pago por la plataforma correspondiente, tu cuenta se actualizará a Premium de forma inmediata. Si experimentas algún retraso, puedes usar el botón 'Sincronizar' en el menú lateral.",
                Icons.Default.FlashOn
            )
        )
    }

    Scaffold { paddingValues ->
        LazyColumn(
            modifier = Modifier
                .fillMaxSize()
                .padding(paddingValues)
                .padding(16.dp),
            verticalArrangement = Arrangement.spacedBy(12.dp)
        ) {
            item {
                Text(
                    "Preguntas Frecuentes",
                    style = MaterialTheme.typography.titleLarge,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.padding(bottom = 8.dp)
                )
                Text(
                    "Información detallada sobre el servicio y la suscripción.",
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.padding(bottom = 16.dp)
                )
            }

            items(infoItems) { item ->
                ExpandableInfoCard(item)
            }

            item {
                Spacer(Modifier.height(24.dp))
                ContactSupportSection()
            }
        }
    }
}

@Composable
private fun ExpandableInfoCard(item: InfoItem) {
    var expanded by remember { mutableStateOf(false) }

    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(12.dp),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.surfaceVariant.copy(alpha = 0.5f)
        )
    ) {
        Column(
            modifier = Modifier
                .clickable { expanded = !expanded }
                .padding(16.dp)
        ) {
            Row(verticalAlignment = Alignment.CenterVertically) {
                Icon(
                    item.icon,
                    contentDescription = null,
                    tint = MaterialTheme.colorScheme.primary,
                    modifier = Modifier.size(24.dp)
                )
                Spacer(Modifier.width(16.dp))
                Text(
                    text = item.question,
                    style = MaterialTheme.typography.titleMedium,
                    fontWeight = FontWeight.Bold,
                    modifier = Modifier.weight(1f)
                )
                Icon(
                    if (expanded) Icons.Default.ExpandLess else Icons.Default.ExpandMore,
                    contentDescription = null
                )
            }

            AnimatedVisibility(visible = expanded) {
                Column {
                    Spacer(Modifier.height(12.dp))
                    HorizontalDivider(color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f))
                    Spacer(Modifier.height(12.dp))
                    Text(
                        text = item.answer,
                        style = MaterialTheme.typography.bodyMedium,
                        lineHeight = 22.sp
                    )
                }
            }
        }
    }
}

@Composable
private fun ContactSupportSection() {
    Card(
        modifier = Modifier.fillMaxWidth(),
        shape = RoundedCornerShape(16.dp),
        colors = CardDefaults.cardColors(
            containerColor = MaterialTheme.colorScheme.primaryContainer
        )
    ) {
        Column(
            modifier = Modifier.padding(20.dp),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Text(
                "¿Aún tenés dudas?",
                style = MaterialTheme.typography.titleLarge,
                fontWeight = FontWeight.Bold
            )
            Text(
                "Nuestro equipo de soporte técnico está disponible para ayudarte.",
                textAlign = androidx.compose.ui.text.style.TextAlign.Center,
                style = MaterialTheme.typography.bodyMedium,
                modifier = Modifier.padding(vertical = 8.dp)
            )
            Spacer(Modifier.height(12.dp))
            
            Button(
                onClick = { openWhatsApp("5493413850100") },
                modifier = Modifier.fillMaxWidth(),
                colors = ButtonDefaults.buttonColors(containerColor = Color(0xFF25D366))
            ) {
                Icon(Icons.AutoMirrored.Filled.Chat, null)
                Spacer(Modifier.width(8.dp))
                Text("WhatsApp Soporte")
            }
            
            OutlinedButton(
                onClick = { openEmail("appjuridicorosario@gmail.com") },
                modifier = Modifier.fillMaxWidth().padding(top = 8.dp)
            ) {
                Icon(Icons.Default.Email, null)
                Spacer(Modifier.width(8.dp))
                Text("Enviar Correo")
            }
        }
    }
}
